import { useState, useEffect, useRef, useContext } from "react"
import { Link } from "react-router-dom"
import { CheckCircle, Sparks, Play, Phone, ArrowRight, SoundHigh, Refresh } from "iconoir-react"
import { Modal, ModalBody } from "flowbite-react"
import mixpanel from "mixpanel-browser"
import ReactGA from "react-ga4"
import ReactPixel from "react-facebook-pixel"
import { settings } from "@/settings"
import { IosTrackingContext } from "../GlobalLayout"
import Transcript from "./calls/Transcript"
import { VibratoClientContext, UserContext } from "./contexts"
import {
  introScript,
  healthcareScript,
  subscriptionScript,
  billNegotiationScript,
  emergencyScript
} from "@/lib/call-tour-script"

// Hardcoded HMACs for the demo scripts. Regenerate if the scripts change.
const scriptHmacs = {
  billNegotiationScript: "97bd007358e198cc15852b0df8bc37893ade579714963eca1cf4243556a9dc72",
  emergencyScript: "b10cf3e70ee2f7c242c4a0fdcff25f42972bb7d804ba725275696ecd3afae743",
  healthcareScript: "6bad77e9395e10351cd4ef64642e2df1a9b0052362c9dfb4ca87a34b6937a287",
  introScript: "659240beec1a5933fcedb1305f7f9b7b1f417bb0991bb0bcb3dacf94cb40a1ca",
  subscriptionScript: "93ca53ee7e43e6b0e10984fa6e0bb99f6ad94de4a2c34ba534a32a92884a561b"
}

const tourSteps = [
  { id: 1, title: "Welcome", subtitle: "Get started" },
  { id: 2, title: "Meet Your AI", subtitle: "Introduction" },
  { id: 3, title: "See Example", subtitle: "Live demo" },
  { id: 4, title: "Get Started", subtitle: "Complete" }
]

// --- Tour Progress Indicator ---
function TourProgressIndicator({
  currentStep,
  onRestart,
  onSkip
}: {
  currentStep: number
  onRestart: () => void
  onSkip: () => void
}) {
  return (
    <div className="w-full bg-white border-t border-gray-200 shadow-sm flex-shrink-0 h-[70px]">
      <div className="max-w-sm mx-auto px-4 h-full flex items-center justify-between">
        <div className="flex items-center justify-between relative flex-1">
          {/* Progress Line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -z-10 transform -translate-y-1/2">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
            />
          </div>

          {tourSteps.map((step) => {
            const isActive = step.id === currentStep
            const isCompleted = step.id < currentStep

            return (
              <div key={step.id} className="flex flex-col items-center relative z-10">
                {/* Step Circle */}
                <div
                  className={`
                  w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300
                  ${
                    isCompleted
                      ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md"
                      : isActive
                        ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md ring-2 ring-purple-100"
                        : "bg-gray-200 text-gray-500"
                  }
                `}
                >
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : step.id}
                </div>

                {/* Active Step Pulse */}
                {isActive && (
                  <div className="absolute -top-0.5 -left-0.5 w-9 h-9 rounded-full bg-purple-200  opacity-20" />
                )}
              </div>
            )
          })}
        </div>

        {/* Action Buttons */}
        <div className="ml-4 flex items-center gap-2">
          <button
            onClick={onSkip}
            className="px-3 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors duration-200"
          >
            Skip
          </button>
          <button
            onClick={onRestart}
            className="px-3 py-1 text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-md transition-colors duration-200"
          >
            Restart
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Audio Permission Modal ---
function AudioPermissionModal({
  show,
  onClose,
  onConfirm
}: {
  show: boolean
  onClose: () => void
  onConfirm: (withAudio: boolean) => void
}) {
  return (
    <Modal show={show} onClose={onClose} size="md" className="pt-safe">
      <ModalBody className="p-0">
        <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-8 text-center text-white rounded-t-lg">
          <div className="mx-auto w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
            <SoundHigh className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Hear the Magic</h2>
          <p className="text-purple-100">
            You're about to hear our AI make a demo phone call. Turn on your sound for the full, amazing experience.
          </p>
        </div>

        <div className="p-8 space-y-4">
          <button
            onClick={() => {
              onConfirm(true)
              mixpanel.track("Call Tour Audio Enabled")
              onClose()
            }}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Demo with Audio
          </button>

          <button
            onClick={() => {
              onConfirm(false)
              mixpanel.track("Call Tour Audio Disabled")
              onClose()
            }}
            className="w-full text-gray-600 font-medium py-3 hover:text-gray-800 transition-colors"
          >
            Continue without audio
          </button>
        </div>
      </ModalBody>
    </Modal>
  )
}

const guidedTasks = [
  {
    text: "Lower my cable/internet bill",
    emoji: "💰",
    link: `/chat?taskDescription=${btoa("Lower my cable/internet bill")}`
  },
  {
    text: "Cancel an unwanted subscription",
    emoji: "❌",
    link: `/chat?taskDescription=${btoa("Cancel an unwanted subscription")}`
  },
  {
    text: "Wait on hold with customer service",
    emoji: "🕒",
    link: `/chat?taskDescription=${btoa("Wait on hold with customer service for me")}`
  }
]

const scenarios = [
  {
    id: "healthcare",
    title: "Book Medical Appointment",
    description: "Skip phone anxiety and scheduling stress",
    emoji: "🩺",
    script: healthcareScript
  },
  {
    id: "subscription",
    title: "Cancel Subscription",
    description: "Avoid retention tactics and runaround",
    emoji: "❌",
    script: subscriptionScript
  },
  {
    id: "billNegotiation",
    title: "Lower Cable Bill",
    description: "Professional negotiation gets results",
    emoji: "💰",
    script: billNegotiationScript
  },
  {
    id: "emergency",
    title: "Emergency Plumbing Service",
    description: "Urgent plumbing situations handled immediately",
    emoji: "🚨",
    script: emergencyScript
  }
]

// --- Scenario Selection Component ---
function ScenarioSelectionComponent({ onScenarioSelect }: { onScenarioSelect: (scenarioId: string) => void }) {
  return (
    <div className="mt-4 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 max-w-4xl mx-auto">
      {scenarios.map((scenario) => (
        <button
          key={scenario.id}
          onClick={() => onScenarioSelect(scenario.id)}
          className="p-3 md:p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg md:rounded-xl hover:from-purple-100 hover:to-indigo-100 hover:border-purple-300 transition-all transform hover:scale-105 text-left"
        >
          <div className="flex items-center gap-2 md:gap-4">
            <div className="text-xl md:text-3xl flex-shrink-0">{scenario.emoji}</div>
            <div className="min-w-0 flex flex-col gap-1">
              <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">{scenario.title}</h3>
              <p className="text-xs md:text-sm text-gray-600 leading-tight">{scenario.description}</p>
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}

// --- Next Button Component ---
function NextButton({ onNext, onPlayAnother }: { onNext: () => void; onPlayAnother: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <button
        onClick={onPlayAnother}
        className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-800"
      >
        <Refresh className="w-4 h-4" />
        <span>See another example</span>
      </button>
      <button
        onClick={onNext}
        className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-lg py-3 px-8 rounded-xl transition-all flex items-center gap-3 transform hover:scale-105 shadow-lg hover:shadow-xl"
      >
        <span>Continue</span>
        <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  )
}

// --- Simplified Reusable Components (from ViewCall.tsx) ---

function MockCallStatusBadge({ status }: { status: "in_progress" | "completed" | "success" }) {
  const config = {
    in_progress: {
      text: "In Progress",
      icon: <Phone className="w-4 h-4" />,
      className: "from-blue-500 to-blue-600"
    },
    completed: {
      text: "Completed",
      icon: <CheckCircle className="w-4 h-4" />,
      className: "from-gray-500 to-gray-600"
    },
    success: {
      text: "Success",
      icon: <CheckCircle className="w-4 h-4" />,
      className: "from-emerald-500 to-green-600"
    }
  }[status]

  return (
    <div
      className={`bg-gradient-to-r ${config.className} text-white font-medium px-4 py-2 rounded-xl shadow-sm flex items-center gap-2`}
    >
      {config.icon}
      {config.text}
    </div>
  )
}

// --- Main CallTour Component ---

export default function CallTour() {
  const vibratoClient = useContext(VibratoClientContext)
  const user = useContext(UserContext)
  const { iosAppTrackingEnabled } = useContext(IosTrackingContext)
  const [stepType, setStepType] = useState<"intro" | "loading" | "scenario_selection" | "running" | "complete">("intro")
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null)
  const [currentScript, setCurrentScript] = useState<any[]>([])
  const [messages, setMessages] = useState<any[]>([])
  const [playlist, setPlaylist] = useState<string[]>([])
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
  const [showAudioModal, setShowAudioModal] = useState(false)
  const [audioEnabledByUser, setAudioEnabledByUser] = useState(true)

  const transcriptEndRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const hasCompletedFlow = useRef(false)

  // Function to determine current step
  const getCurrentStep = () => {
    if (stepType === "intro") return 1
    if (stepType === "loading" && !selectedScenario) return 2
    if (stepType === "running" && currentScript === introScript) return 2
    if (stepType === "scenario_selection") return 2
    if (stepType === "loading" && selectedScenario) return 3
    if (stepType === "running" && currentScript !== introScript) return 3
    if (stepType === "complete") return 4
    return 1
  }

  // When the tour is completed, mark it as such on the backend
  useEffect(() => {
    if (stepType === "complete" && !hasCompletedFlow.current) {
      hasCompletedFlow.current = true
      mixpanel.track("Call Tour Completed")
      vibratoClient.completeIntroFlow().catch((err: any) => {
        console.error("Failed to mark intro flow as complete", err)
      })
      if (user) {
        const tourCompletedKey = `vibrato_intro_tour_completed_${user.id}`
        localStorage.setItem(tourCompletedKey, "true")
      }

      if (settings.IS_IOS && !iosAppTrackingEnabled) {
        return
      }

      ReactGA.event("call_tour_completed", {
        method: "call_tour_completed",
        content: "call_tour_page"
      })
    }
  }, [stepType, vibratoClient, user])

  const handlePlayAnotherExample = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    mixpanel.track("Call Tour Play Another Example")
    setSelectedScenario(null)
    setCurrentScript(introScript)
    setMessages([
      {
        id: introScript.length + 1,
        speaker: "ai",
        text: "Curious for more? Select another scenario.",
        isFinal: true,
        component: <ScenarioSelectionComponent onScenarioSelect={handleScenarioSelect} />
      }
    ])
    setPlaylist([])

    setCurrentMessageIndex(introScript.length)
    setStepType("scenario_selection")
  }

  // This effect orchestrates the playing of the demo script
  useEffect(() => {
    if (stepType !== "running" || playlist.length === 0 || currentScript.length === 0) {
      return
    }

    // Check if we are finished with the script
    // if (currentMessageIndex >= currentScript.length) {
    //   // Transition to complete after a short delay
    //   setTimeout(() => {
    //     setStepType("complete")
    //   }, 1000)
    //   return
    // }

    // Guard to ensure we only process each message once
    if (messages.length === currentMessageIndex) {
      const currentMessage = currentScript[currentMessageIndex]
      if (!currentMessage) {
        return
      }

      const audioUrl = playlist[currentMessageIndex]
      const audio = audioRef.current

      const playAudio = () => {
        // Add the next message to the transcript
        const newMessage = {
          id: currentMessageIndex,
          speaker: currentMessage.speaker,
          text: currentMessage.display_text,
          isFinal: true,
          ...(currentMessage.component && { component: currentMessage.component })
        }

        setMessages((prev) => [newMessage, ...prev])

        const messageWaitTime = 3000

        // Play the corresponding audio
        if (audio && audioEnabledByUser) {
          audio.src = audioUrl
          audio
            .play()
            .then(() => {
              console.log("Audio playback succeeded.")
            })
            .catch((e) => {
              console.error("Audio playback failed, advancing to next step.", e)
              // If play fails for any reason (e.g. browser policy), wait 3 seconds before advancing
              setTimeout(() => {
                setCurrentMessageIndex((prevIndex) => prevIndex + 1)
              }, messageWaitTime)
            })
        } else {
          console.log("Audio is disabled, advancing to next step.")
          setTimeout(() => {
            setCurrentMessageIndex((prevIndex) => prevIndex + 1)
          }, messageWaitTime)
        }
      }

      // Wait for the specified delay before playing the audio
      if (currentMessage?.delay) {
        setTimeout(playAudio, currentMessage.delay)
      } else {
        playAudio()
      }
    }
  }, [stepType, playlist, currentMessageIndex, messages.length, currentScript, audioEnabledByUser])

  // This effect sets up the event listener that advances the script upon audio completion
  useEffect(() => {
    const audio = audioRef.current

    const handleAudioEnded = () => {
      setCurrentMessageIndex((prevIndex) => prevIndex + 1)
    }

    if (audio) {
      audio.addEventListener("ended", handleAudioEnded)
    }

    // Cleanup listener on component unmount
    return () => {
      if (audio) {
        audio.removeEventListener("ended", handleAudioEnded)
      }
    }
  }, []) // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleAudioConfirm = (withAudio: boolean) => {
    setShowAudioModal(false)
    setAudioEnabledByUser(withAudio)

    if (withAudio && audioRef.current) {
      // This is the "unlock" trick.
      // By playing a silent sound directly in the user interaction handler,
      // we satisfy Safari's autoplay policy. The audio element can now be
      // controlled programmatically later, even after async operations.
      const audio = audioRef.current
      audio.src =
        "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJhdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8gTG93IFJldkJ1ZmZlciBvZiB0aGUgS2V5cy9GaXJzdCBvZiB0aGUgUGF1c2UgVGhlbWVzIC8="
      audio.play().catch(() => {
        /* Ignore error, this is just for unlocking */
      })
      audio.pause()
      audio.currentTime = 0
    }

    handleStartDemo(withAudio)
  }

  const handleStartDemo = async (withAudio: boolean) => {
    setStepType("loading")
    mixpanel.track("Call Tour Started", { withAudio })
    try {
      // Convert intro script to API format
      const introScriptForApi = introScript.map(({ text, voice_id }) => ({ text, voice_id }))
      const hmac = scriptHmacs.introScript
      const response = await vibratoClient.createCallTourPlaylist(introScriptForApi, hmac)
      setPlaylist(response.playlist)

      // Preload the first audio file
      if (withAudio && response.playlist.length > 0 && audioRef.current) {
        audioRef.current.src = response.playlist[0]
        audioRef.current.preload = "auto"
      }

      setCurrentScript(introScript)
      setMessages([]) // Clear any previous messages
      setCurrentMessageIndex(0) // Reset the script index

      setStepType("running")
    } catch (error) {
      console.error("Failed to create call tour playlist:", error)
      // TODO: Add user-facing error state
      setStepType("intro") // Reset on error
    }
  }

  const handleStartTourClick = () => {
    setShowAudioModal(true)
  }

  const handleScenarioSelect = async (scenarioId: string) => {
    const scenario = scenarios.find((s) => s.id === scenarioId)
    if (!scenario) return

    mixpanel.track("Call Tour Scenario Selected", { scenarioId })
    setStepType("loading")
    setSelectedScenario(scenarioId)

    try {
      // Convert scenario script to API format
      const scenarioScriptForApi = scenario.script.map(({ text, voice_id }) => ({ text, voice_id }))
      const hmac = scriptHmacs[`${scenarioId}Script` as keyof typeof scriptHmacs]
      const response = await vibratoClient.createCallTourPlaylist(scenarioScriptForApi, hmac)
      setPlaylist(response.playlist)
      setCurrentScript(scenario.script)
      setMessages([]) // Clear previous messages
      setCurrentMessageIndex(0) // Reset the script index

      setStepType("running")
    } catch (error) {
      console.error("Failed to create call tour playlist:", error)
      // TODO: Add user-facing error state
      setStepType("intro") // Reset on error
    }
  }

  // Check if we should show scenario selection
  useEffect(() => {
    if (stepType === "running" && currentScript === introScript && currentMessageIndex >= introScript.length) {
      // Intro script is complete, add scenario selection as a message
      setTimeout(() => {
        setMessages((prev) => [
          {
            id: introScript.length + 1,
            speaker: "ai",
            text: "Choose a scenario to see me in action:",
            isFinal: true,
            component: <ScenarioSelectionComponent onScenarioSelect={handleScenarioSelect} />
          },
          ...prev
        ])
        setStepType("scenario_selection")
      }, 1000)
    }
  }, [stepType, currentScript, currentMessageIndex, setStepType])

  // Check if we should show the next button after scenario completion
  useEffect(() => {
    if (
      stepType === "running" &&
      currentScript !== introScript &&
      selectedScenario &&
      currentMessageIndex >= currentScript.length
    ) {
      // Scenario script is complete, add next button as a message
      setTimeout(() => {
        setMessages((prev) => [
          {
            id: currentScript.length + 1,
            speaker: "ai",
            text: "Ready to get started with your own AI assistant?",
            isFinal: true,
            component: <NextButton onNext={() => setStepType("complete")} onPlayAnother={handlePlayAnotherExample} />
          },
          ...prev
        ])
      }, 1000)
    }
  }, [stepType, currentScript, selectedScenario, currentMessageIndex, setStepType])

  // Signup tracking for new users visiting call tour
  useEffect(() => {
    if (!user || !user.created_at) {
      return
    }

    // Check iOS tracking permission
    if (settings.IS_IOS && !iosAppTrackingEnabled) {
      return
    }

    // Check if user account is older than a week
    const createdAtDate = new Date(user.created_at)
    const oneWeekAgo = new Date()
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

    if (createdAtDate < oneWeekAgo) {
      // User is older than a week, don't track
      return
    }

    // Check session storage to avoid re-emitting
    const sessionKey = `vibrato_call_tour_signup_tracked_${user.id}`
    if (sessionStorage.getItem(sessionKey)) {
      // Already tracked in this session
      return
    }

    try {
      // Track Facebook Pixel signup event
      ReactPixel.track("CompleteRegistration", {
        content_name: "Call Tour Visit",
        content_category: "User Onboarding",
        value: 1.0,
        currency: "USD"
      })

      // Track Google Analytics signup event
      ReactGA.event("call_tour_visit", {
        method: "call_tour_visit",
        content: "call_tour_page"
      })

      // Set session storage flag to prevent re-emitting
      sessionStorage.setItem(sessionKey, "true")

      console.log("Tracked signup events for call tour visit", {
        user_id: user.id,
        created_at: user.created_at
      })
    } catch (error) {
      console.error("Failed to track signup events:", error)
    }
  }, [user, iosAppTrackingEnabled])

  const renderIntro = () => (
    <div className="flex flex-col h-full items-center justify-center sm:h-fit text-center bg-white p-4 sm:p-12 sm:rounded-2xl sm:shadow-2xl max-w-3xl mx-auto border border-gray-100">
      <div className="mx-auto w-fit p-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg mb-6">
        <Sparks className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">See the Magic in 30 Seconds</h1>
      <p className="text-base md:text-lg text-gray-600 mt-4 max-w-xl mx-auto">
        Press play to watch a live, interactive demo of your new AI assistant making a phone call. It's the best way to
        see how Vibrato works.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3 text-gray-500">
        <SoundHigh />
        <span className="text-sm md:text-base font-medium">Make sure your sound is on!</span>
      </div>
      <div className="mt-6 w-full flex justify-center">
        <button
          onClick={handleStartTourClick}
          className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-base md:text-lg py-4 px-10 rounded-xl transition-all flex items-center justify-center gap-3 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          <Play className="w-6 h-6" />
          <span>Start Interactive Demo</span>
        </button>
      </div>
      <p className="text-xs md:text-sm text-gray-500 mt-4">
        We'll add <span className="font-bold text-green-600">20 free demo bonus minutes</span> to your account after the
        demo.
      </p>
    </div>
  )

  const renderLoading = () => (
    <div className="text-center bg-white p-8 md:p-12 rounded-2xl shadow-2xl max-w-3xl mx-auto border border-gray-100">
      <div className="flex justify-center items-center gap-4">
        <svg className="animate-spin h-8 w-8 text-purple-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Generating your demo...</h2>
      </div>
      <p className="text-base md:text-lg text-gray-600 mt-4">This may take a few moments. Please wait.</p>
    </div>
  )

  const renderCompletion = () => (
    <div className="max-h-fit h-full w-full overflow-auto text-center bg-white p-8 md:p-12 sm:rounded-2xl sm:shadow-2xl max-w-4xl mx-auto  border-gray-200 animate-fade-in">
      <div className="mx-auto w-fit p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg mb-6">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">That's how easy it is! ✨</h1>
      <p className="text-base md:text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
        Your AI assistant can handle calls just like that, saving you hours of time. We've added{" "}
        <span className="font-bold text-green-600">20 free demo bonus minutes</span> to your account.
      </p>
      <div className="mt-8 border-t pt-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Ready to solve a real problem?</h2>
        <p className="text-sm md:text-base text-gray-500 mt-1">Select a popular task or tell us what you need.</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {guidedTasks.map((task) => (
            <Link
              key={task.text}
              to={task.link}
              className="block w-full text-center px-6 py-4 border-2 border-transparent text-base md:text-lg font-bold rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-700 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all transform hover:-translate-y-1"
            >
              <span role="img" aria-label="emoji" className="mr-2">
                {task.emoji}
              </span>
              {task.text}
            </Link>
          ))}
        </div>
        <div className="mt-6 flex flex-col items-center gap-4">
          <Link
            to="/call-library"
            className="inline-flex items-center gap-2 text-base md:text-lg font-medium text-gray-700 hover:text-gray-900"
          >
            I have something else in mind...
            <ArrowRight />
          </Link>
          <button
            onClick={handlePlayAnotherExample}
            className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-800"
          >
            <Refresh className="w-4 h-4" />
            <span>Watch another example</span>
          </button>
        </div>
      </div>
      <div className="mt-8 border-t pt-8">
        <div className="mx-auto w-fit p-3 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl shadow-lg mb-4">
          <Phone className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Want to talk to the AI yourself?</h2>
        <p className="text-sm md:text-base text-gray-500 mt-1 max-w-xl mx-auto">
          Experience the magic firsthand. Call our AI for a mind-bending conversation. It's a fun glimpse into the
          future of AI voices.
        </p>
        <div className="mt-4">
          <div className="inline-block bg-gray-100 rounded-lg px-4 py-3">
            <a
              href={`tel:${settings.DEMO_PHONE_NUMBER_TEL}`}
              className="text-lg md:text-xl font-mono text-gray-700 tracking-wider hover:underline focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded"
            >
              {settings.DEMO_PHONE_NUMBER}
            </a>
          </div>
        </div>
      </div>
    </div>
  )

  const renderCallView = (isComplete = false) => (
    <div className="w-full h-full flex flex-col animate-fade-in">
      {/* Header Section */}
      <div className="hidden  flex-row flex-wrap justify-between items-center gap-4 mb-6 px-4">
        <div className=" flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Phone width="24" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">Live Demo Call</h1>
            <p className="text-sm md:text-base text-gray-600">You are watching a simulated call.</p>
          </div>
        </div>

        <div className="hidden md:flex flex-wrap items-center gap-3">
          <MockCallStatusBadge status={isComplete ? "success" : "in_progress"} />
          {/* {callStartTime && <MockTotalTimeButton startTime={callStartTime} isRunning={!isComplete} />} */}
        </div>
      </div>

      {/* Transcript */}
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex flex-1 h-full overflow-hidden">
          <Transcript
            key="transcript-demo"
            call={{}} // Mock call object
            messages={messages}
            otherPartyName=""
            showSendMessage={false}
            onSendMessage={() => {}}
            showSpeakerPill={true}
            inCard={false}
          />
          <div ref={transcriptEndRef} />
        </div>
      </div>
    </div>
  )

  return (
    <div className="h-full w-full overflow-auto bg-gray-50 flex flex-col">
      <audio ref={audioRef} />
      <div className="flex-1 flex justify-center items-center sm:p-4" style={{ height: "calc(100% - 80px)" }}>
        {stepType === "intro" && renderIntro()}
        {stepType === "loading" && renderLoading()}
        {(stepType === "running" || stepType === "scenario_selection") && renderCallView()}
        {stepType === "complete" && renderCompletion()}
      </div>
      <TourProgressIndicator
        currentStep={getCurrentStep()}
        onRestart={() => {
          // Stop any playing audio
          if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
          }
          mixpanel.track("Call Tour Restarted")

          setStepType("intro")
          setSelectedScenario(null)
          setCurrentScript([])
          setMessages([])
          setPlaylist([])
          setCurrentMessageIndex(0)
        }}
        onSkip={() => {
          // Stop any playing audio
          if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
          }
          mixpanel.track("Call Tour Skipped")

          // Go directly to the completion step
          setStepType("complete")
        }}
      />
      <AudioPermissionModal
        show={showAudioModal}
        onClose={() => setShowAudioModal(false)}
        onConfirm={handleAudioConfirm}
      />
    </div>
  )
}
